import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { crmApi } from "../../lib/crm-api";
import { clustersOf } from "../../lib/duplicate-clusters";
import { queryKeys } from "../../lib/queryKeys";
import { Button } from "../../components/ui/Button";
import { DuplicateCluster } from "../../components/crm/DuplicateCluster";
import { cn } from "../../components/ui/cn";
import type { User } from "../../types";

/**
 * Suspected duplicate companies, for a person to resolve.
 *
 * Nothing on this page happens by itself. Detection only ever writes rows here;
 * a merge runs when somebody presses the button and not before. Two branches of
 * the same wholesaler share a domain, a VAT number and usually a name, and an
 * automatic merge would quietly destroy one rep's book of business — so the
 * expensive, irreversible half is a manager's, and the cheap, reversible half
 * (dismissing, or saying they are one group) is anybody's.
 *
 * ## One card per domain, not one per flag
 *
 * The page is a list of clusters and nothing else. It used to be a list of
 * flags, which is how the detector stores a suspicion and not how anybody
 * reads one: four unfiled depots on rexel.co.uk are six flags, so the page
 * drew a grouping panel listing all four twice and then six near-identical
 * cards underneath, each naming two of the same four companies. Ten companies
 * filled three screens.
 *
 * `DuplicateCluster` is the whole page now, shared with the banner on a company
 * page — which is where most of these are actually noticed.
 *
 * The "Not linked to Team" section that used to sit at the bottom has gone. It
 * was built from flags with one side, which the detector stopped writing when
 * that question got its own queue -- since gone too, because linking to Team
 * is done from Team's side. It had been an empty heading on a page that
 * already had too much on it.
 */

const TABS = [
  { value: "open", label: "To review" },
  { value: "dismissed", label: "Dismissed" },
  { value: "merged", label: "Merged" },
] as const;

export function Duplicates({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<"open" | "dismissed" | "merged">("open");

  const duplicates = useQuery({
    queryKey: queryKeys.crmDuplicates(status),
    queryFn: () => crmApi.duplicates(status),
    staleTime: 30_000,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["crm", "duplicates"] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.crmCompaniesRoot });
  };

  const rescan = useMutation({ mutationFn: crmApi.rescanDuplicates, onSuccess: refresh });

  const clusters = clustersOf(duplicates.data?.duplicates ?? []);

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
        <h1 className="text-sm font-semibold text-content">Possible duplicates</h1>
        {/* What is on the page, rather than how many rows are behind it: the
            count used to be flags -- eight, over three things to look at. Only
            on the queue itself, where it is work outstanding; beside
            "Dismissed" it would be counting history. */}
        {status === "open" ? (
          <span className="text-xs text-faint">{clusters.length}</span>
        ) : null}

        <div className="ml-2 flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setStatus(tab.value)}
              className={cn(
                "rounded-full px-2.5 py-1 text-xs",
                status === tab.value
                  ? "bg-brand-600 text-white"
                  : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {user.role === "admin" ? (
          <Button
            size="sm"
            variant="secondary"
            className="ml-auto"
            disabled={rescan.isPending}
            onClick={() => rescan.mutate()}
            title="Re-checks every company. Wanted once, after switching the CRM on."
          >
            {rescan.isPending ? "Scanning…" : "Rescan all"}
          </Button>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {duplicates.isLoading ? (
          <p className="p-8 text-center text-xs text-faint">Loading…</p>
        ) : clusters.length === 0 ? (
          <Empty status={status} />
        ) : (
          <div className="mx-auto flex max-w-4xl flex-col gap-3">
            {clusters.map((cluster) => (
              <DuplicateCluster
                key={cluster.key}
                cluster={cluster}
                canMerge={user.role === "admin"}
                onChange={refresh}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Says why the list is empty, which differs by tab. */
function Empty({ status }: { status: "open" | "dismissed" | "merged" }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
      <p className="text-sm font-medium text-muted">
        {status === "open"
          ? "Nothing flagged"
          : status === "dismissed"
            ? "Nothing dismissed"
            : "Nothing merged"}
      </p>
      <p className="max-w-sm text-xs text-faint">
        {status === "open"
          ? "Companies are re-checked on every edit, on their website domain. Anything suspicious appears here for a person to decide — never merged automatically."
          : "Answers you have already given appear here, so a decision can be read back later."}
      </p>
    </div>
  );
}
